import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import { initializeStore } from '@/redux/initialize-store';
import { addAlert } from '@/redux/slices/primeur.slice';
import { getI18NextConfigForLocale } from '@/utils/locales';
import { isFeatureEnabled, sanitizeObject } from '@/utils/millesimaUtils';
import { prefetchLayout } from '@/utils/prefetchLayout';
import { getAvailableAlerts, getPrimeurLinksConfig } from '@/utils/primeurUtils';

import { useAppDispatch, useAppSelector } from '@/redux';
import { NS } from '@/constants';
import { usePrimeurUser } from '@/hooks';

import { RETURN_BUTTON_ID } from '@/components/account/ReturnButton';
import SideBar from '@/components/common/SideBar';
import MetaTagsMillesima from '@/components/layout/MetaTagsMillesima';
import PrimeurAlert from '@/components/primeurAlert/PrimeurAlert';
import PrimeurSearch from '@/components/primeurAlert/PrimeurSearch';
import PrimeurPreferences from '@/components/primeurAlert/PrimeurPreferences';

const ModalAlertPrimeurInfos = dynamic(() => import('@/components/primeurAlert/ModalAlertPrimeurInfos'));
ModalAlertPrimeurInfos.displayName = 'ModalAlertPrimeurInfos';

import styles from './primeur.module.scss';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { FEATURES } from '@/constants/features';
import { PRIMEUR_TABS } from '@/constants/primeur';
import { PAGES } from '@/constants/pages';
import { FOOTER_TYPE } from '@/constants/footer';

const NAMESPACES = [
  NS.COMMON,
  NS.ADMIN,
  NS.CATEG,
  NS.CHECKOUT
];

export const Primeur = ({
  designations, hashUserFromQuery
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { link } = router.query;
  const dispatch = useAppDispatch();
  usePrimeurUser({ hashUserFromQuery, currentLink: link });

  const { alertPrimeurInfosModalVisible } = useAppSelector(({ gui }) => gui);
  const { primeurUser } = useAppSelector(({ primeur }) => primeur);
  const { storeId } = useAppSelector(({ root }) => root.storeConf);

  const isMobileOrTablet = useScreenSizeContext().isMobileOrTablet;

  const onAlertMe = (alerts, name, isAllChecked) => {
    dispatch(addAlert({ alerts, name, isAllChecked, storeId }));
  };

  const onReturn = useCallback(() => {
    if (isMobileOrTablet) {
      router.push(PAGES.PRIMEUR);
    }
  }, [isMobileOrTablet]);

  const content = useMemo(() => {
    switch (link) {
      case PRIMEUR_TABS.SEARCH:
        return (
          <PrimeurSearch
            designations={designations}
            alertList={primeurUser?.alerts}
            onAlertMe={onAlertMe}
            onReturn={onReturn}
            isMobileOrTablet={isMobileOrTablet}
          />
        );
      case PRIMEUR_TABS.ALERTS:
        return (
          <PrimeurAlert
            designations={designations}
            alertList={primeurUser?.alerts}
            onAlertMe={onAlertMe}
            onReturn={onReturn}
            isMobileOrTablet={isMobileOrTablet}
          />
        );
      case PRIMEUR_TABS.PREFERENCES:
        return (
          <PrimeurPreferences
            onReturn={onReturn}
            isMobileOrTablet={isMobileOrTablet}
          />
        );
    }
  }, [router.query, primeurUser?.alerts, onAlertMe, onReturn, designations, link, isMobileOrTablet]);

  return (
    <>
      <MetaTagsMillesima defaultTag title={t('alert.link')} />

      <div className={styles['primeur-page']} data-rbf="primeur-page">
        {!isMobileOrTablet &&
          <h1 className={styles.header}>
            {t('alert.link').toUpperCase()}
          </h1>
        }
        <div className={styles.container}>
          {!isMobileOrTablet ? (
            <div>
              <SideBar
                linksConfig={getPrimeurLinksConfig(getAvailableAlerts(designations, primeurUser?.alerts).length)}
                baseUrlPath={PAGES.PRIMEUR}
              />
            </div>
          ) : null}

          <div className={styles.content}>
            <div id="primeur-container" className={styles['primeur-container']}>
              <div id={RETURN_BUTTON_ID} />
              {content}
            </div>
          </div>
        </div>
        {alertPrimeurInfosModalVisible && (
          <ModalAlertPrimeurInfos isOpen={alertPrimeurInfosModalVisible} />
        )}
      </div>
    </>
  );
};

Primeur.propTypes = {
  designations: PropTypes.object,
  hashUserFromQuery: PropTypes.string
};

export default Primeur;

export const getServerSideProps = (async (ctx) => {
  const store = initializeStore({ ssrContext: ctx });
  const layoutOptions = await prefetchLayout(ctx, store);
  const { root: { storeConf } } = store.getState();
  const { productsService } = store.services;
  const { query } = ctx;
  const { hashUser: hashUserFromQuery } = query;
  const { locale, features, url } = storeConf || ctx;
  const translate = await serverSideTranslations(locale, NAMESPACES, getI18NextConfigForLocale(locale));
  const currentPage = `/${ctx.resolvedUrl.split('/')[1].split('?')[0]}`;

  if (!isFeatureEnabled({ features }, FEATURES.PRIMEUR_CAMPAIGN) && currentPage.includes(PAGES.PRIMEUR)) {
    return {
      redirect: {
        destination: url
      }
    };
  }

  let designations = {};
  try {
    designations = await productsService.getProductInAlert(storeConf.storeId);
  } catch (error) {
    console.error('PrimeurAlert.getServerSideProps error', error);
  }

  return {
    props: sanitizeObject({
      ...translate,
      designations,
      hashUserFromQuery,
      layoutOptions: {
        ...layoutOptions,
        footerType: FOOTER_TYPE.PRIMEUR
      },
      initialReduxState: store.getState()
    })
  };
});
